import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { UiConfig } from 'motion-master-client';
import { concatAll, delay, filter, map } from 'rxjs/operators';

import { getDeviceFileStatus, upsertDeviceFile } from './device-file.actions';
import {
  addDeviceParameterTraces,
  updateDeviceParameterTrace,
  updateDeviceParameterTraces,
  updateDeviceParameterTraceSetting,
} from './device-parameter-trace.actions';
import {
  addDeviceParameters,
  clearDeviceParametersInterfaceUnits,
  getDeviceParameterValuesStatus,
  setDeviceParametersInterfaceUnitsFromUiConfig,
  updateDeviceParameter,
  updateDeviceParameters,
} from './device-parameter.actions';

import { deviceParameterValuesCompleted, getDeviceFileErrored } from '@app/models/status-resolve';
import { DeviceParameterTraceService } from '@app/services/device-parameter-trace.service';
import { getCurrentValue } from '@app/util';

@Injectable()
export class DeviceParameterTraceEffects {
  private actions$ = inject(Actions);
  private deviceParameterTraceService = inject(DeviceParameterTraceService);

  insertDefaultDeviceParameterTracesOnGetDeviceParameterValuesStatusCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDeviceParameterValuesStatus),
      filter(
        ({ initiator, deviceParameterValues }) =>
          initiator === addDeviceParameters.type && deviceParameterValuesCompleted(deviceParameterValues),
      ),
      filter((action) => {
        const deviceParameterTraces = getCurrentValue(
          this.deviceParameterTraceService.selectDeviceParameterTracesByDevice(action.device),
        );
        return deviceParameterTraces.length === 0;
      }),
      delay(0), // ensures that the effect that updates parameter values is executed before this effect, guarantees that parameter values are not empty
      map((action) => {
        return addDeviceParameterTraces({
          deviceParameterTraces: this.deviceParameterTraceService.buildDefaultDeviceParameterTracesForDevice(
            action.device,
          ),
        });
      }),
    ),
  );

  // Early initialize graph when ui.config.json is either:
  // - Not found
  // - Put in store and interface unit `feed` property is not present
  handleDeviceSetGraphInitializedWhenUiConfigFeedIsNotSet$ = createEffect(() =>
    this.actions$.pipe(
      ofType(getDeviceFileStatus, upsertDeviceFile),
      filter(
        (action) =>
          (action.deviceFile.name === 'ui.config.json' && getDeviceFileErrored(action.deviceFile)) ||
          action.deviceFile.name === 'ui.config.json',
      ),
      map(({ deviceFile }) => {
        try {
          return JSON.parse(deviceFile?.content ?? '');
        } catch (err) {
          return {} as UiConfig;
        }
      }),
      filter((uiConfig) => !uiConfig.interfaceUnit?.feed),
      map(() => {
        return updateDeviceParameterTraceSetting({
          setting: {
            graphInitialized: true,
          },
        });
      }),
    ),
  );

  // Initialize graph when ui.config.json is put in store (`feed` property is present)
  // (This means that ESI file is parsed and custom unit is applied to appropriate parameters)
  updateDefaultDeviceParameterTracesOnUpdateDeviceParameter$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateDeviceParameters),
      filter(
        ({ initiator }) =>
          initiator === setDeviceParametersInterfaceUnitsFromUiConfig.type ||
          initiator === clearDeviceParametersInterfaceUnits.type,
      ),
      map((action) => [
        updateDeviceParameterTraces({
          updates: this.deviceParameterTraceService.updateTraceUnits(action.device),
        }),
        updateDeviceParameterTraceSetting({
          setting: {
            graphInitialized: true,
          },
        }),
      ]),
      concatAll(),
    ),
  );

  // When any of the device parameters' unit are updated, update the corresponding trace
  updateDeviceParameterTraceOnUpdateDeviceParameterUnit$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateDeviceParameter),
      filter(({ update }) => update.changes?.unit !== undefined),
      map(({ update }) =>
        updateDeviceParameterTrace({
          update: {
            changes: {
              unit: update.changes.unit,
            },
            id: String(update.id),
          },
        }),
      ),
    ),
  );
}
